import type { LocationDevice } from "app/pages/digital-marketing/keywords-campaigns/shared/types";

import type { SavedGscMarket } from "../../types";

// BOTH first — it's the default device for a new market, so it should be preferred
// when multiple options are available (e.g. after switching to a conflict-free country).
const ALL_DEVICES: readonly LocationDevice[] = ["BOTH", "DESKTOP", "MOBILE"];

/**
 * Returns which devices are unavailable for a given country based on already-saved markets.
 *
 * Conflict rules:
 *  - DESKTOP  blocks  DESKTOP + BOTH
 *  - MOBILE   blocks  MOBILE  + BOTH
 *  - BOTH     blocks  DESKTOP + MOBILE + BOTH
 */
export const getDisabledDevices = (countryCode: string, otherMarkets: SavedGscMarket[]): LocationDevice[] => {
    const disabled = new Set<LocationDevice>();

    for (const market of otherMarkets) {
        if (market.countryCode !== countryCode) {
            continue;
        }

        disabled.add(market.device);

        if (market.device === "BOTH") {
            disabled.add("DESKTOP");
            disabled.add("MOBILE");
        } else {
            disabled.add("BOTH");
        }
    }

    return [...disabled];
};

/**
 * Returns the first device that is NOT disabled, or `undefined` if all are taken.
 */
export const getFirstAvailableDevice = (disabledDevices: LocationDevice[]): LocationDevice | undefined => {
    const disabledSet = new Set(disabledDevices);
    return ALL_DEVICES.find((d) => !disabledSet.has(d));
};

/**
 * Returns country codes where all three devices are effectively taken,
 * meaning the country should be hidden from the location picker.
 */
export const getHiddenCountryCodes = (otherMarkets: SavedGscMarket[]): string[] => {
    const countryCodes = new Set(otherMarkets.map((m) => m.countryCode));

    return [...countryCodes].filter((code) => {
        const disabled = getDisabledDevices(code, otherMarkets);
        return disabled.length >= ALL_DEVICES.length;
    });
};
